import { configureStore, combineReducers } from '@reduxjs/toolkit';
import authReducer from './slices/authSlice'; // Global user identity
import gamesReducer from './slices/gamesSlice';
import movesReducer from './slices/movesSlice';
import tournamentsReducer from './slices/tournamentsSlice';
import leaderboardReducer from './slices/leaderboardSlice';
import websocketReducer from './slices/websocketSlice';
import uiReducer from './slices/uiSlice';
import gameSessionReducer from './slices/gameSessionSlice'; // GOLDEN STANDARD: Event-driven state machine
import navigationReducer from './slices/navigationSlice';
import poolReducer from './slices/poolSlice';
import poolTournamentReducer from './slices/poolTournamentSlice';
import poolSessionReducer from './slices/poolSessionSlice';
import websocketMiddleware from './middleware/websocketMiddleware';

// Create root reducer
const rootReducer = combineReducers({
  auth: authReducer, // Global user identity - available to all middleware/reducers
  games: gamesReducer, // LEGACY: To be migrated to gameSession
  moves: movesReducer,
  tournaments: tournamentsReducer, // Keeps tournament metadata (not session state)
  leaderboard: leaderboardReducer,
  websocket: websocketReducer,
  ui: uiReducer,
  gameSession: gameSessionReducer, // GOLDEN STANDARD: Single source of truth for game session lifecycle
  navigation: navigationReducer,
  pool: poolReducer,
  poolTournament: poolTournamentReducer,
  poolSession: poolSessionReducer
});

// Infer RootState type from rootReducer
export type RootState = ReturnType<typeof rootReducer>;

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types that may contain non-serializable data
        ignoredActions: ['websocket/messageReceived'],
        // Ignore these paths in the state
        ignoredPaths: ['moves.processedMoveIds']
      }
    }).concat(websocketMiddleware as any),
  devTools: process.env.NODE_ENV !== 'production' || window.location.hostname === 'localhost' ? {
    maxAge: 50, // Keep last 50 actions
    trace: true,
    actionSanitizer: (action: any) => {
      // Sanitize sensitive data in actions
      if (action.type === 'user/login' || action.type === 'auth/login') {
        return {
          ...action,
          payload: {
            ...action.payload,
            password: '[REDACTED]',
            token: '[REDACTED]'
          }
        };
      }
      return action;
    },
    stateSanitizer: (state: any) => {
      // Sanitize sensitive data in state
      if (state.auth) {
        return {
          ...state,
          auth: {
            ...state.auth,
            token: state.auth.token ? '[REDACTED]' : null,
            refreshToken: state.auth.refreshToken ? '[REDACTED]' : null
          }
        };
      }
      return state;
    }
  } : false
});

// Infer AppDispatch type from store
export type AppDispatch = typeof store.dispatch;
